import React, { useRef, useState } from 'react';
import { getLocale, useIntl } from '@edx/frontend-platform/i18n';
import { NavLink } from 'react-router-dom';

import { useHeaderFooter } from '../../hooks';

import './index.scss';
import messages from './messages';

import contactHeroImg from '../../img/contact/CONTACTUS-LWC.jpg';
import {
  completionCertificateImages,
  courseContentBannerImages,
  courseContentCoreCommitmentsImages,
  editProfileImages,
  forgotPasswordImages,
  introductionCourseContentImages,
  profileCertificatesImages,
  registerAccountImages,
  signupAccount2Images,
  signupAccountImages,
} from '../../img/faq';
import { DEFAULT_LANGUAGE, getLocalizedImage } from '../../utils';
import { getFaqVideoUrl } from './faqVideo';
import { useFaqVideoCaptions } from './useFaqVideoCaptions';

interface FAQItemProps {
  question: React.ReactNode;
  answer: React.ReactNode;
}

interface FaqProps {
  children?: React.ReactNode;
}

const FAQItem = ({ question, answer }: FAQItemProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  return (
    <div className="faq-item">
      <button className="faq-question" type="button" onClick={() => setIsOpen(!isOpen)}>
        <div className="faq-marker" />
        <h3>{question}</h3>
        <div className={`faq-icon ${isOpen ? 'open' : ''}`}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 9L12 15L18 9" stroke="#8B7EB5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </button>
      {isOpen && (
        <div className="faq-answer">
          <div>{answer}</div>
        </div>
      )}
    </div>
  );
};

const Faq: React.FC<FaqProps> = ({ children }) => {
  const { headerContent, footerContent } = useHeaderFooter(children);
  const intl = useIntl();
  const locale = getLocale();
  const videoRef = useRef<HTMLIFrameElement>(null);
  const [subtitlesOn, setSubtitlesOn] = useState<boolean>(locale !== DEFAULT_LANGUAGE);
  const captions = useFaqVideoCaptions(videoRef, locale, subtitlesOn);

  const navLinkFaq = (chunks: React.ReactNode) => <NavLink className="faq-link" to="/">{chunks}</NavLink>;
  const boldText = (chunks: React.ReactNode) => <b>{chunks}</b>;
  const mailToLink = (chunks: React.ReactNode) => <a className="faq-link no-line-break" href={`mailto:${chunks}`}>{chunks}</a>;

  const faqData: Array<{ question: React.ReactNode; answer: React.ReactNode }> = [
    {
      question: intl.formatMessage(messages.qRegisterAccount),
      answer: (
        <ul>
          <li>
            <p>
              {intl.formatMessage(messages.aRegisterAccountStep1, {
                link: navLinkFaq,
              })}
              <br />
              <img
                src={getLocalizedImage(registerAccountImages)}
                alt={intl.formatMessage(messages.aRegisterAccountStep1Alt)}
              />
            </p>
          </li>
          <li>
            <p>
              {intl.formatMessage(messages.aRegisterAccountStep2)}
              <br />
              <img
                src={getLocalizedImage(signupAccountImages)}
                alt={intl.formatMessage(messages.aRegisterAccountStep2Alt1)}
              />
              <img
                src={getLocalizedImage(signupAccount2Images)}
                alt={intl.formatMessage(messages.aRegisterAccountStep2Alt2)}
              />
            </p>
          </li>
        </ul>
      ),
    },
    {
      question: intl.formatMessage(messages.qChangePassword),
      answer: (
        <ul>
          <li>
            <p>
              {intl.formatMessage(messages.aChangePasswordStep1, {
                bold: boldText,
              })}
              <br />
              <img
                src={getLocalizedImage(registerAccountImages)}
                alt={intl.formatMessage(messages.aChangePasswordStep1Alt)}
              />
            </p>
          </li>
          <li>
            <p>
              {intl.formatMessage(messages.aChangePasswordStep2, {
                bold: boldText,
              })}
            </p>
          </li>
          <li>
            <p>
              {intl.formatMessage(messages.aChangePasswordStep3, {
                bold: boldText,
              })}
              <img
                src={getLocalizedImage(forgotPasswordImages)}
                alt={intl.formatMessage(messages.aChangePasswordStep3Alt)}
              />
            </p>
          </li>
        </ul>
      ),
    },
    {
      question: intl.formatMessage(messages.qTroubleSignIn),
      answer: (
        <ul>
          <li><p>{intl.formatMessage(messages.aTroubleSignInStep1)}</p></li>
          <li><p>{intl.formatMessage(messages.aTroubleSignInStep2)}</p></li>
          <li><p>{intl.formatMessage(messages.aTroubleSignInStep3)}</p></li>
          <li>
            <p>
              {intl.formatMessage(messages.aTroubleSignInStep4, {
                bold: boldText,
              })}
              <img
                src={getLocalizedImage(forgotPasswordImages)}
                alt={intl.formatMessage(messages.aTroubleSignInStep4Alt)}
              />
            </p>
          </li>
        </ul>
      ),
    },
    {
      question: intl.formatMessage(messages.qVideosStopped),
      answer: (
        <ul>
          <li><p>{intl.formatMessage(messages.aVideosStoppedStep1)}</p></li>
          <li><p>{intl.formatMessage(messages.aVideosStoppedStep2)}</p></li>
          <li><p>{intl.formatMessage(messages.aVideosStoppedStep3)}</p></li>
          <li><p>{intl.formatMessage(messages.aVideosStoppedStep4)}</p></li>
        </ul>
      ),
    },
    {
      question: intl.formatMessage(messages.qCantProgress),
      answer: (
        <ul>
          <li>
            <p>
              {intl.formatMessage(messages.aCantProgressStep1)}
              <img
                src={getLocalizedImage(introductionCourseContentImages)}
                alt={intl.formatMessage(messages.aCantProgressStep1Alt)}
              />
              {intl.formatMessage(messages.aCantProgressStep2)}
              <img
                src={getLocalizedImage(courseContentCoreCommitmentsImages)}
                alt={intl.formatMessage(messages.aCantProgressStep2Alt1)}
              />
              <img
                src={getLocalizedImage(courseContentBannerImages)}
                alt={intl.formatMessage(messages.aCantProgressStep2Alt2)}
              />
            </p>
          </li>
        </ul>
      ),
    },
    {
      question: intl.formatMessage(messages.qContentNotLoad),
      answer: (
        <ul>
          <li><p>{intl.formatMessage(messages.aContentNotLoadStep1)}</p></li>
          <li><p>{intl.formatMessage(messages.aContentNotLoadStep2)}</p></li>
          <li><p>{intl.formatMessage(messages.aContentNotLoadStep3)}</p></li>
          <li><p>{intl.formatMessage(messages.aContentNotLoadStep4)}</p></li>
          <li><p>{intl.formatMessage(messages.aContentNotLoadStep5)}</p></li>
        </ul>
      ),
    },
    {
      question: intl.formatMessage(messages.qFindCertificate),
      answer: (
        <ul>
          <li>
            <p>
              {intl.formatMessage(messages.aFindCertificateStep1, {
                bold: boldText,
              })}
              <img
                src={getLocalizedImage(completionCertificateImages)}
                alt={intl.formatMessage(messages.aFindCertificateStep1Alt)}
              />
            </p>
          </li>
          <li>
            <p>
              {intl.formatMessage(messages.aFindCertificateStep2, {
                bold: boldText,
              })}
              <img
                src={getLocalizedImage(profileCertificatesImages)}
                alt={intl.formatMessage(messages.aFindCertificateStep2Alt)}
              />
            </p>
          </li>
          <li>
            <p>
              {intl.formatMessage(messages.aFindCertificateStep3)}
            </p>
          </li>
        </ul>
      ),
    },
    {
      question: intl.formatMessage(messages.qNameIncorrect),
      answer: (
        <ul>
          <li>
            <p>
              {intl.formatMessage(messages.aNameIncorrect)}
              <img
                src={getLocalizedImage(editProfileImages)}
                alt={intl.formatMessage(messages.aNameIncorrectAlt)}
              />
            </p>
          </li>
        </ul>
      ),
    },
    {
      question: intl.formatMessage(messages.qStayUpToDate),
      answer: (
        <p>
          {intl.formatMessage(messages.aStayUpToDate)}
          <br />
          LinkedIn:{' '}
          <a
            className="faq-link"
            href="https://www.linkedin.com/company/LeadingWithCharacter/"
            target="_blank"
            rel="noreferrer"
          >
            https://www.linkedin.com/company/LeadingWithCharacter/
          </a>
          <br />
          X:{' '}
          <a
            className="faq-link"
            href="https://x.com/LWCCourse"
            target="_blank"
            rel="noreferrer"
          >
            https://x.com/LWCCourse
          </a>
        </p>
      ),
    },
    {
      question: intl.formatMessage(messages.qStillIssues),
      answer: (
        <p>
          {intl.formatMessage(messages.aStillIssues, {
            link: mailToLink,
          })}
        </p>
      ),
    },
  ];

  return (
    <>
      <input type="hidden" id="anPageName" name="page" defaultValue="about" />
      <div className="container-center-horizontal">
        <div className="about screen contact">
          <div className="homepage ">
            {headerContent}
            <div className="overlap-group6">
              <div className="hero-image">
                <div className="overlap-group-1">
                  <img
                    className="overlap-group-item"
                    src={contactHeroImg}
                    alt=""
                  />
                  <h1 className="about-us recklessneue-regular-normal-white-100-1px">
                    <span className="recklessneue-regular-normal-white-100-1px">
                      {intl.formatMessage(messages.faqTitle)}
                    </span>
                  </h1>
                </div>
              </div>
            </div>
          </div>
          <div className="faq-container">
            <div className="video-container">
              <iframe
                ref={videoRef}
                width="560"
                height="315"
                src={getFaqVideoUrl(locale)}
                title={intl.formatMessage(messages.youtubeIframeTitle)}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
            {captions.hasCaptions && (
              <div className="faq-video-captions">
                {subtitlesOn && <p className="faq-video-captions-text" lang={locale}>{captions.text}</p>}
                <button
                  type="button"
                  className="faq-video-captions-toggle"
                  aria-pressed={subtitlesOn}
                  onClick={() => setSubtitlesOn(!subtitlesOn)}
                >
                  {intl.formatMessage(subtitlesOn ? messages.subtitlesHide : messages.subtitlesShow)}
                </button>
              </div>
            )}
            <div className="faq-list">
              {faqData.map((item, idx) => (
                <FAQItem
                  key={item.question ? item.question.toString() : idx}
                  question={item.question}
                  answer={item.answer}
                />
              ))}
            </div>
          </div>
          {footerContent}
        </div>
      </div>
    </>
  );
};

export default Faq;
